import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { twoFaAPI } from '../api/2fa';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedField, ThemedInput, ThemedNotice, ThemedPanel, ThemedSubmitButton, ThemedBottomActionBar } from '../components/ui/ThemedSurface';

type Status = 'loading' | 'disabled' | 'enabling' | 'enabled';

function statusLabel(t: ReturnType<typeof useTranslation>['t'], status: Status) {
  if (status === 'enabled') return t('twoFactor.enabled');
  if (status === 'enabling') return t('twoFactor.settingUp');
  return t('twoFactor.notEnabled');
}

function StatusPanel({ status }: { status: Exclude<Status, 'loading'> }) {
  const { t } = useTranslation();
  const active = status !== 'disabled';
  const color = active ? C.gold : C.muted;

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${active ? C.gold : C.softBorder} 50%, transparent 100%)` }} />
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs" style={{ color: C.muted }}>{t('twoFactor.currentStatus')}</div>
            <div className="mt-1 truncate text-xl font-semibold leading-none" style={{ color }}>
              {statusLabel(t, status)}
            </div>
          </div>
          <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: active ? `${C.gold}66` : C.softBorder, background: active ? C.chipBg : C.inputBg, color, boxShadow: active ? `0 0 20px ${C.gold}18` : undefined }}>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3 5 6v5c0 4.2 2.7 8.1 7 10 4.3-1.9 7-5.8 7-10V6l-7-3Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d={active ? 'm9 12 2 2 4-5' : 'M9.5 9.5 14.5 14.5M14.5 9.5 9.5 14.5'} />
            </svg>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

function CodePanel({ label, code, onCodeChange }: { label: string; code: string; onCodeChange: (value: string) => void }) {
  return (
    <>
      <ThemedField label={label}>
        <ThemedInput
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ''))}
          className="text-center text-2xl tracking-[0.45em]"
          placeholder="000000"
        />
      </ThemedField>
    </>
  );
}



export function TwoFactor() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('loading');
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await twoFaAPI.getStatus();
        setStatus(res.enabled ? 'enabled' : 'disabled');
      } catch {
        setStatus('disabled');
      }
    })();
  }, []);

  const handleEnable = async () => {
    setError('');
    setSubmitting(true);
    try {
      const res: { secret?: string; otpauth_uri?: string } = await twoFaAPI.enable();
      setSecret(res.secret ?? '');
      setOtpauthUri(res.otpauth_uri ?? '');
      setStatus('enabling');
    } catch (e: any) {
      setError(e?.message ?? t('twoFactor.generateKeyFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    if (code.length !== 6) { setError(t('twoFactor.enter6DigitCode')); return; }
    setSubmitting(true);
    try {
      await twoFaAPI.verify({ code });
      setStatus('enabled');
      setSecret('');
      setOtpauthUri('');
      setCode('');
    } catch (e: any) {
      setError(e?.message ?? t('twoFactor.verifyFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    setError('');
    if (code.length !== 6) { setError(t('twoFactor.enter6DigitCode')); return; }
    setSubmitting(true);
    try {
      await twoFaAPI.disable({ code });
      setStatus('disabled');
      setCode('');
    } catch (e: any) {
      setError(e?.message ?? t('twoFactor.disableFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('twoFactor.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: status === 'loading' ? '12px' : '76px' }}>
          {status === 'loading' ? (
            <LoadingSkeleton variant="card" />
          ) : (
            <>
              <StatusPanel status={status} />

              {status === 'enabling' && (
                <ThemedPanel className="p-4">
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                      <div className="text-sm font-semibold" style={{ color: C.text }}>{t('twoFactor.addKey')}</div>
                      <div className="break-all rounded-xl border px-3 py-2 font-mono text-xs" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.text }}>{secret}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="text-sm" style={{ color: C.text }}>{t('twoFactor.copyUri')}</div>
                      <div className="break-all rounded-xl border px-3 py-2 font-mono text-[10px]" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>{otpauthUri}</div>
                    </div>
                    <CodePanel label={t('twoFactor.enterFromApp')} code={code} onCodeChange={setCode} />
                  </div>
                </ThemedPanel>
              )}

              {status === 'enabled' && (
                <>
                  <CodePanel label={t('twoFactor.enterToDisable')} code={code} onCodeChange={setCode} />
                </>
              )}

              {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
            </>
          )}
        </div>
        {status === 'disabled' && (
          <ThemedBottomActionBar>
            <ThemedSubmitButton disabled={submitting} onClick={() => void handleEnable()}>
              {submitting ? '…' : t('twoFactor.enable')}
            </ThemedSubmitButton>
          </ThemedBottomActionBar>
        )}
        {status === 'enabling' && (
          <ThemedBottomActionBar>
            <ThemedSubmitButton disabled={code.length !== 6 || submitting} onClick={() => void handleVerify()}>
              {submitting ? '…' : t('twoFactor.confirmEnable')}
            </ThemedSubmitButton>
          </ThemedBottomActionBar>
        )}
        {status === 'enabled' && (
          <ThemedBottomActionBar>
            <button type="button" disabled={code.length !== 6 || submitting} onClick={() => void handleDisable()} className="h-11 w-full rounded-xl border text-sm font-semibold transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60" style={{ borderColor: `${C.sell}55`, background: `${C.sell}1f`, color: C.sell }}>
              {submitting ? '…' : t('twoFactor.disable')}
            </button>
          </ThemedBottomActionBar>
        )}
      </div>
    </div>
  );
}
